import { appSettingsSchema, type AppSettingsInput } from '@hirebotiq-ops/core';
import type { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { AdminLayout } from '../components/AdminLayout.js';
import { Button } from '../components/ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card.js';
import { Field } from '../components/ui/field.js';
import { Input } from '../components/ui/input.js';
import { requireRole } from '../lib/auth-guard.js';
import { trpc } from '../lib/trpc.js';

export const Route = createFileRoute('/admin/settings')({
  component: AdminSettings,
  beforeLoad: async ({ context }) => {
    await requireRole(context.queryClient, ['ceo', 'internal_staff']);
  },
});

function AdminSettings() {
  const utils = trpc.useUtils();
  const settings = trpc.settings.get.useQuery();

  const updateMutation = trpc.settings.update.useMutation({
    onSuccess: () => {
      toast.success('Einstellungen und Feature Flags gespeichert.');
      utils.settings.get.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  // Drei Generics: Schema-Defaults machen den INPUT optional, der Resolver
  // liefert den OUTPUT (alles Pflicht) — sonst kollidieren die Resolver-Typen.
  const form = useForm<z.input<typeof appSettingsSchema>, unknown, AppSettingsInput>({
    resolver: zodResolver(appSettingsSchema),
    defaultValues: {
      enableAbsenceDecisionSupport: true,
      enableAdminKpiDashboard: true,
      defaultAnnualVacationDays: 30,
      absenceWarningThresholdDays: 5,
    },
  });

  const { register, handleSubmit, setValue, watch, formState, reset } = form;

  useEffect(() => {
    if (settings.data) {
      reset(settings.data);
    }
  }, [settings.data, reset]);

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-3xl">
        <header>
          <h1 className="text-3xl font-bold">System-Einstellungen &amp; Feature Flags</h1>
          <p className="text-[var(--muted-foreground)]">
            Konfiguration operativer Parameter und Umschalten von UX- &amp; Decision-Support-Modulen.
          </p>
        </header>

        <form onSubmit={handleSubmit((v) => updateMutation.mutate(v))} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>🚩 Feature Flags (Modul-Steuerung)</CardTitle>
              <CardDescription>
                Aktiviere oder deaktiviere erweiterte UX-Hilfen für Disponenten und Staff.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-md border border-[var(--border)] bg-[var(--card)]">
                <div>
                  <div className="font-semibold text-sm">Disponenten-Entscheidungshilfe bei Abwesenheiten</div>
                  <div className="text-xs text-[var(--muted-foreground)]">
                    Blendet beim Bearbeiten/Freigeben von Urlaubsanträgen den Resturlaubssaldo &amp; Einsatzkonflikte ein.
                  </div>
                </div>
                <input
                  type="checkbox"
                  className="h-5 w-5 rounded accent-[var(--primary)]"
                  checked={watch('enableAbsenceDecisionSupport')}
                  onChange={(e) => setValue('enableAbsenceDecisionSupport', e.target.checked)}
                />
              </div>

              <div className="flex items-center justify-between p-3 rounded-md border border-[var(--border)] bg-[var(--card)]">
                <div>
                  <div className="font-semibold text-sm">Staff &amp; CEO KPI Dashboard</div>
                  <div className="text-xs text-[var(--muted-foreground)]">
                    Aktiviert die Live-Kennzahlen und Quick-Actions auf dem Hauptdashboard (/admin).
                  </div>
                </div>
                <input
                  type="checkbox"
                  className="h-5 w-5 rounded accent-[var(--primary)]"
                  checked={watch('enableAdminKpiDashboard')}
                  onChange={(e) => setValue('enableAdminKpiDashboard', e.target.checked)}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>⚙️ Operative Schwellenwerte &amp; Vorgaben</CardTitle>
              <CardDescription>
                Voreinstellungen für Urlaubskontingente und Warnhinweise.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <Field
                label="Standard-Jahresurlaub (Tage)"
                htmlFor="defaultAnnualVacationDays"
                error={formState.errors.defaultAnnualVacationDays?.message}
              >
                <Input
                  id="defaultAnnualVacationDays"
                  type="number"
                  min={0}
                  max={100}
                  {...register('defaultAnnualVacationDays', { valueAsNumber: true })}
                />
              </Field>

              <Field
                label="Schwellenwert Resturlaub-Warnung (Tage)"
                htmlFor="absenceWarningThresholdDays"
                error={formState.errors.absenceWarningThresholdDays?.message}
              >
                <Input
                  id="absenceWarningThresholdDays"
                  type="number"
                  min={1}
                  max={30}
                  {...register('absenceWarningThresholdDays', { valueAsNumber: true })}
                />
              </Field>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Speichere …' : 'Einstellungen speichern'}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}
